import { useEffect, useState } from 'react';
import { notFound } from 'next/navigation';
import { isFalsy } from 'utility-types';
import { getAppStoreById, getPosts } from '@/api/appstore.api';
import type { AppStore } from '@/interfaces/service/appstore/appstore.interface';
import { baseAppStorePostFilter, useAppStoreStore } from '@/stores/appstore.store';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { setRequestPostParams } from '@/services/appstore.service';
import { POST_PAGGE_SIZE } from '@/constants/appstore/post.constants';
import { setBasePropsAppStore } from '@/utils/appstore.util';
import { getToken } from '@/utils/auth.util';
import Loading from '@/components/loading';
import GoToJoinAppStorePostsCareService from '@/views/appstore/components/GoToJoinAppStorePostsCareService';
import NoPostDataEndCrawling from '@/views/appstore/components/NoPostDataEndCrawling';
import NoPostDataOnCrawling from '@/views/appstore/components/NoPostDataOnCrawling';
import BaseDashboardContents from '@/views/appstore/dashboard/components/BaseDashboardContents';
import DashboardHeader from '@/views/appstore/dashboard/components/DashboardHeader';
import DashboardSectionContainer from '@/views/appstore/dashboard/components/DashboardSectionContainer';

const AppStoreDashboardCaseResolver = ({ appStoreId, isMobile }: { appStoreId: number; isMobile: boolean }) => {
  const [isCheckedReview, setIsCheckedReview] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const [currentAppStore, setCurrentAppStore] = useState<AppStore>();

  const [postTotalCount, setPostTotalCount] = useState(0);

  const { appStores, setAppStorePosts } = useAppStoreStore((state) => state);

  const { getUserStoreId } = useFavoriteStore();

  const [hasReviewNoPost, setHasReviewNoPost] = useState((appStores[appStoreId]?.postData?.posts ?? []).length === 0);

  const getDashboardComponent = (): JSX.Element => {
    if (!currentAppStore) {
      if (appStoreId > -1) {
        return notFound();
      }
      return <GoToJoinAppStorePostsCareService isMobile={isMobile} />;
    } else if (currentAppStore.status === 'STABLE') {
      if (hasReviewNoPost) {
        return <NoPostDataEndCrawling isMobile={isMobile} />;
      } else {
        return <BaseDashboardContents targetAppStore={currentAppStore} isMobile={isMobile} />;
      }
    } else {
      if (hasReviewNoPost) {
        return (
          <DashboardSectionContainer isMobile={isMobile}>
            <NoPostDataOnCrawling isMobile={isMobile} onCrawlingAppStoreId={currentAppStore.storeId} />
          </DashboardSectionContainer>
        );
      } else {
        return <BaseDashboardContents targetAppStore={currentAppStore} isMobile={isMobile} />;
      }
    }
  };

  useEffect(() => {
    if (!currentAppStore) {
      const checkReviews = () => {
        const token = getToken();
        const _appStores = appStores;
        const isAppStoresNotEmpty = Object.keys(_appStores)?.length > 0;

        if (isAppStoresNotEmpty && !currentAppStore) {
          if (appStoreId > -1) {
            setCurrentAppStore(_appStores[appStoreId]);
          } else {
            const appStoreKey = Number(Object.keys(_appStores)[0]);
            setCurrentAppStore(_appStores[appStoreKey]);
          }
        }

        setIsCheckedReview(true);
      };
      checkReviews();
    }
  }, [appStores, appStoreId, currentAppStore]);

  useEffect(() => {
    if (currentAppStore && currentAppStore.channels && isCheckedReview) {
      const checkPosts = async () => {
        const basePageData = {
          pageNo: 1,
          pageSize: POST_PAGGE_SIZE
        };

        const params = setRequestPostParams({ ...currentAppStore, filter: baseAppStorePostFilter }, basePageData);
        const postData = await getPosts(params);

        if (postData) {
          const { data, sliceValue = basePageData, totalCount } = postData;
          const resolvedTotalCount = totalCount ?? 0;

          setHasReviewNoPost(data?.length === 0);
          setPostTotalCount(resolvedTotalCount);
          setAppStorePosts(currentAppStore.storeId, data, sliceValue, resolvedTotalCount);
        }
      };
      checkPosts();
    }
    setIsLoaded(true);
  }, [currentAppStore, isCheckedReview, setAppStorePosts]);

  if (!isLoaded || !isCheckedReview) {
    return <Loading />;
  }

  return (
    <>
      {currentAppStore ? <DashboardHeader appStoreId={appStoreId} totalReviewCount={postTotalCount} /> : null}
      {getDashboardComponent()}
    </>
  );
};

export default AppStoreDashboardCaseResolver;
